import type { TFunction } from 'i18next';

import {
  CreateUpdateContractEstimateItemDto,
  GetContractEstimateItemDto,
  GetContractExtendedDto,
  GetEstimateItemContractDto,
  GetEstimateItemDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import mathUtils from '../../../utils/mathUtils';
import { TKeys } from '../../../common/hooks/useTranslation';

export const getSavedItem = (
  item: CreateUpdateContractEstimateItemDto,
  savedContractEstimateItems?: GetContractEstimateItemDto[],
) =>
  savedContractEstimateItems?.find(
    ({ estimateItem }) => estimateItem.id === item.estimateItemId,
  );

export const getContractItem = (
  availableEstimateItemId: string,
  contractEstimateItems: CreateUpdateContractEstimateItemDto[],
) =>
  contractEstimateItems.find(
    it => it.estimateItemId === availableEstimateItemId,
  ) || ({ amount: '0' } as CreateUpdateContractEstimateItemDto);

export const getNotAssignedAmount = (
  estimateItem: GetEstimateItemContractDto,
) =>
  mathUtils.subtract(
    mathUtils.round(estimateItem.purchaseNetTotal),
    estimateItem.contractEstimateItems
      ?.filter(o => o.active)
      ?.map(o => o.amount)
      .reduce((a, b) => mathUtils.add(a, b), 0) || 0,
  );

export interface SumData {
  positionsTotalSum: number;
  positionsNotAssignedSum: number;
  contractSettledAmount: number;
  contractAmount: number;
  contractUnsettledAmount: number;
}

export const getSumData = (
  contractEstimateItems: CreateUpdateContractEstimateItemDto[],
  availableEstimateItems: GetEstimateItemDto[],
  filteredAvailableEstimateItems: GetEstimateItemContractDto[],
  extendedContract?: GetContractExtendedDto,
): SumData => {
  let positionsTotalSum = 0;
  let positionsNotAssignedSum = 0;
  let contractSettledAmount = 0;
  const amountToExcludeFromMax = 0;

  for (const availableEstimateItem of filteredAvailableEstimateItems) {
    if (availableEstimateItem) {
      positionsTotalSum = mathUtils.add(
        positionsTotalSum,
        availableEstimateItem.purchaseNetTotal,
      );

      positionsNotAssignedSum = mathUtils.add(
        positionsNotAssignedSum,
        mathUtils.subtract(
          mathUtils.round(availableEstimateItem.purchaseNetTotal),
          availableEstimateItem.contractEstimateItems
            ?.filter(o => o.active)
            ?.map(o => o.amount)
            .reduce((a, b) => mathUtils.add(a, b), 0) || 0,
        ),
      );
    }
  }
  for (const availableEstimateItem of availableEstimateItems) {
    const item = contractEstimateItems.find(
      ({ estimateItemId }) => estimateItemId === availableEstimateItem.id,
    );

    if (item) {
      contractSettledAmount = mathUtils.add(contractSettledAmount, item.amount);
    }
  }

  // const availableEstimateItemsIds = availableEstimateItems.map(({ id }) => id);
  // contractEstimateItems.forEach(estimateItem => {
  //   if (!availableEstimateItemsIds.includes(estimateItem.estimateItemId)) {
  //     amountToExcludeFromMax = mathUtils.add(
  //       amountToExcludeFromMax,
  //       estimateItem.amount,
  //     );
  //   }
  // });

  return {
    positionsTotalSum,
    positionsNotAssignedSum,
    contractSettledAmount,
    contractAmount: +(extendedContract?.amount || '0'),
    contractUnsettledAmount: mathUtils.subtract(
      +(extendedContract?.amount || '0'),
      contractSettledAmount,
      amountToExcludeFromMax,
    ),
  };
};

export const checkAmountInfo = (
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  max?: string | number,
  amount?: string | number,
) => {
  if (max === '.') {
    max = 0;
  }
  if (amount === '.') {
    amount = 0;
  }
  const amountDiff = mathUtils.subtract(
    mathUtils.round(max, 2),
    mathUtils.round(amount, 2),
  );
  const info = t(tk.contract.sumExceedsContractAmount);
  if (
    mathUtils.parse(mathUtils.round(max, 2)) *
      mathUtils.parse(mathUtils.round(amountDiff, 2)) <
    0
  ) {
    return [info];
  }

  return [];
};

export const handleAmountChangeWithoutChangeState = (
  estimateItemId: string,
  item: CreateUpdateContractEstimateItemDto | undefined,
  value: string | null | undefined,
  privateContractEstimateItems: CreateUpdateContractEstimateItemDto[],
  contractId?: string,
) => {
  if (value?.trim() === '.' || value?.trim() === ',') {
    value = '0';
  }
  const amount = value || '0';
  let updatedItems = privateContractEstimateItems;

  if (
    value !== '0' &&
    privateContractEstimateItems?.some(
      o => o.estimateItemId === estimateItemId,
    ) &&
    !!item?.estimateItemId
  ) {
    const updatedItems = privateContractEstimateItems.map(estimateItem => {
      if (estimateItem.estimateItemId === item.estimateItemId) {
        return { ...estimateItem, ...{ ...item, amount } };
      }

      return estimateItem;
    });

    return updatedItems;
  }
  if (value !== '0') {
    updatedItems = [
      ...privateContractEstimateItems,
      {
        contractId: contractId,
        estimateItemId,
        amount,
      } as CreateUpdateContractEstimateItemDto,
    ].filter(({ amount }) => mathUtils.round(amount) !== 0);
  }
  if (
    privateContractEstimateItems?.some(
      o => o.estimateItemId === estimateItemId,
    ) &&
    !!item?.estimateItemId &&
    value === '0'
  ) {
    updatedItems = privateContractEstimateItems.filter(
      o => o.estimateItemId !== estimateItemId,
    );
  }
  return updatedItems;
};
